import { LiteQuote, SHIPPING_METHOD } from '@frontend/api';

import {
  PAYMENT_METHOD,
  SHIPPING_CARRIER,
  SHIPPING_GROUPING,
  SHIPPING_TYPE,
} from 'constants/all';

import { LOCATION_CHANGE } from 'store/app/types';
import { RootState } from 'store/configureStore';

import { percentage } from 'utils/numberUtils';
import { isValidEmail } from 'utils/stringUtils';

import {
  BILLING_INFO_TYPES,
  CUSTOMER_INFO_STEPS,
  DRAWER_MODES,
  SHIPPING_INFO_TYPES,
  SHIPPING_METHOD_RATES,
} from '../constants';
import { types } from './actions';
import {
  FacilitateOrderDrawerAction,
  FacilitateOrderDrawerState,
} from './reducer.type';
import { AdditionalCharge, SelectedOption } from './types';
import { convertFacilityToShippingInformation } from './utils';

const initialState: FacilitateOrderDrawerState = {
  account: undefined,
  additionalCharges: {},
  adjustments: {},
  billingAddress: undefined,
  billingInformation: null,
  billingInformationType: BILLING_INFO_TYPES.NONE,
  canEstimateShipping: undefined,
  card: undefined,
  confirmationEmail: undefined,
  confirmPaymentTerms: false,
  customer: undefined,
  customerInfoStep: CUSTOMER_INFO_STEPS.PAYMENT_METHOD,
  deliveryOption: undefined,
  drawerMode: DRAWER_MODES.CREATE,
  searchQuery: undefined,
  filteredQuoteItems: undefined,
  expandedStep: undefined,
  facility: undefined,
  isAddressValidationModalOpen: false,
  isCreatingOrder: false,
  isFacilityDrawerOpen: false,
  isFetchingShippingRates: false,
  isOpen: false,
  isQuoteReadOnly: false,
  isSettingOptions: false,
  isShowingShippingAddressForm: false,
  isShowingBillingAddressForm: false,
  isValidatingCC: false,
  maxSupplierFacilities: 1,
  originalAddress: undefined,
  quoteItems: [],
  paymentMethod: undefined,
  pointOfContact: undefined,
  purchaseOrderNumber: undefined,
  purchaseOrderFile: undefined,
  selectedOptions: {},
  shippingAccount: undefined,
  shippingInformationType: SHIPPING_INFO_TYPES.NONE,
  shippingInformation: undefined,
  shippingRate: undefined,
  shippingRates: [],
  shippingType: undefined,
  stripeToken: undefined,
  suggestedAddresses: [],
  supplierFacilityId: undefined,
  supplierFacilities: [],
  tab: 'part_selection',
  useShippingAddress: false,
  visitedCustomerInformationSteps: {},
  withConfirmation: false,
  customerShippingAccount: {
    number: undefined,
    carrier: SHIPPING_CARRIER.UPS,
    method: SHIPPING_METHOD.EARLY_OVERNIGHT,
    grouping: SHIPPING_GROUPING.BULK,
  },
  adjustedLeadTimes: {},
  adjustedShipsOn: {},
  expandedCustomerInfoStepIndices: [],
};

const reducer = (
  state = initialState,
  action:
    | FacilitateOrderDrawerAction
    | { type: typeof LOCATION_CHANGE; payload: { location: Location } },
) => {
  switch (action.type) {
    case LOCATION_CHANGE:
      if (!action.payload.location.pathname.includes('/quotes/edit')) {
        return { ...initialState };
      }
      return state;
    case types.OPEN_FACILITATE_ORDER_DRAWER:
      return {
        ...initialState,
        isOpen: true,
        drawerMode: action.payload.drawerMode,
      };
    case types.OPEN_FACILITATE_ORDER_DRAWER_ERROR:
      return {
        ...state,
        isOpen: false,
      };
    case types.CLOSE_FACILITATE_ORDER_DRAWER:
      return {
        ...state,
        isOpen: false,
      };
    case types.RECEIVE_FACILITATE_ORDER_DRAWER_DATA: {
      const supplierFacilityMap = new Map(
        action.payload.supplierFacilities.map((supplierFacility) => [
          supplierFacility.id,
          supplierFacility.name,
        ]),
      );
      return {
        ...state,
        account: action.payload.account,
        contact: action.payload.contact,
        isQuoteReadOnly: action.payload.isQuoteReadOnly,
        maxSupplierFacilities: action.payload.maxSupplierFacilities,
        quoteItems: action.payload.quoteItems,
        supplierFacilities: supplierFacilityMap,
        supplierFacilityId: action.payload.supplierFacilityId,
        withConfirmation: action.payload?.withConfirmation ?? false,
      };
    }
    case types.RECEIVE_INITIAL_VALUES:
      return {
        ...state,
        adjustedLeadTimes: action.payload.adjustedLeadTimes,
        adjustedShipsOn: action.payload.adjustedShipsOn,
        billingAddress: action.payload.billingAddress,
        billingInformation: action.payload.billingInformation,
        confirmationEmail: action.payload.confirmationEmail,
        facility: action.payload.facility,
        paymentMethod: action.payload.paymentMethod,
        pointOfContact: action.payload.pointOfContact,
        purchaseOrderNumber: action.payload.purchaseOrderNumber,
        selectedOptions: action.payload.selectedOptions,
        shippingAccount: action.payload.shippingAccount,
        shippingInformation: action.payload.shippingInformation,
        shippingRate: action.payload.shippingRate,
        shippingType: action.payload.shippingType,
        withConfirmation: action.payload.withConfirmation,
      };
    case types.SELECT_OPTION: {
      const newSelectedOptions = { ...state.selectedOptions };
      const newAdjustments = { ...state.adjustments };
      const { quoteItemId } = action.payload;

      const adjustedLeadTime = newAdjustments[quoteItemId]?.leadTime
        ? Number(newAdjustments[quoteItemId]?.leadTime)
        : null;
      const adjustedShipsOn = newAdjustments[quoteItemId]?.shipsOn;
      delete newAdjustments[action.payload.quoteItemId];

      if ('quantity' in action.payload) {
        const adjustedLeadTimeKey =
          action.payload.expedite?.id ?? action.payload.quantity.id;
        const manualLeadTime =
          adjustedLeadTime ?? state.adjustedLeadTimes[adjustedLeadTimeKey];
        const manualShipsOn =
          adjustedShipsOn ?? state.adjustedShipsOn[adjustedLeadTimeKey];

        newSelectedOptions[quoteItemId] = {
          quantity: action.payload.quantity,
          expedite: action.payload.expedite,
          addOns: action.payload.addOns,
          manualLeadTime,
          shipsOn: manualShipsOn || null,
        };
      } else {
        delete newSelectedOptions[quoteItemId];
      }

      return {
        ...state,
        selectedOptions: newSelectedOptions,
        adjustments: newAdjustments,
      };
    }
    case types.BULK_SELECT_OPTION: {
      const newSelectedOptions = action.payload;
      const newAdjustments = {};

      return {
        ...state,
        selectedOptions: newSelectedOptions,
        adjustments: newAdjustments,
      };
    }
    case types.SET_TAB:
      return {
        ...state,
        tab: action.payload.tab,
      };
    case types.ADDRESS_VALIDATION_MODAL_OPEN:
      return {
        ...state,
        isAddressValidationModalOpen: true,
        originalAddress: action.payload.originalAddress,
        suggestedAddresses: action.payload.suggestedAddresses,
      };
    case types.ADDRESS_VALIDATION_MODAL_CLOSE:
      return {
        ...state,
        isAddressValidationModalOpen: false,
        originalAddress: null,
        suggestedAddresses: [],
      };
    case types.SET_BILLING_ADDRESS: {
      const { billingAddress } = action.payload;
      const visitedSteps = { ...state.visitedCustomerInformationSteps };
      visitedSteps[CUSTOMER_INFO_STEPS.SHIPPING_OPTIONS_AND_PRICING_SUMMARY] =
        true;
      return {
        ...state,
        billingAddress,
        billingInformationType: billingAddress
          ? BILLING_INFO_TYPES.BILLING_ADDRESS
          : BILLING_INFO_TYPES.NONE,
        useShippingAddress: false,
        customerInfoStep:
          CUSTOMER_INFO_STEPS.SHIPPING_OPTIONS_AND_PRICING_SUMMARY,
        visitedCustomerInformationSteps: visitedSteps,
        isShowingBillingAddressForm: false,
      };
    }
    case types.SET_EDIT_BILLING_ADDRESS: {
      return {
        ...state,
        billingAddress: action.payload.address,
        isShowingBillingAddressForm: true,
      };
    }
    case types.SET_SHIPPING_CHOICE: {
      const visitedSteps = { ...state.visitedCustomerInformationSteps };
      visitedSteps[CUSTOMER_INFO_STEPS.PAYMENT_DETAILS] = true;
      return {
        ...state,
        shippingAccount: action.payload.shippingAccount,
        shippingRate: action.payload.shippingRate,
        shippingType: action.payload.shippingType,
        customerInfoStep: CUSTOMER_INFO_STEPS.PAYMENT_DETAILS,
        visitedCustomerInformationSteps: visitedSteps,
      };
    }
    case types.SET_EDIT_SHIPPING_ADDRESS: {
      return {
        ...state,
        shippingAddress: action.payload.address,
        isShowingBillingAddressForm: true,
      };
    }
    case types.SET_FACILITY: {
      const visitedSteps = { ...state.visitedCustomerInformationSteps };
      visitedSteps[CUSTOMER_INFO_STEPS.BILLING_ADDRESS] = true;
      const shouldShowBillingAddressForm =
        (state.paymentMethod == PAYMENT_METHOD.CREDIT_CARD &&
          state.account &&
          state.account.billingAddresses.filter(
            (address) => address.address && address.address.address1,
          ).length === 0) ||
        (state.paymentMethod == PAYMENT_METHOD.CREDIT_CARD && !state.account);
      return {
        ...state,
        facility: action.payload.facility,
        customerInfoStep: CUSTOMER_INFO_STEPS.BILLING_ADDRESS,
        shippingInformationType: action.payload.facility
          ? SHIPPING_INFO_TYPES.FACILITY
          : SHIPPING_INFO_TYPES.NONE,
        visitedCustomerInformationSteps: visitedSteps,
        isShowingShippingAddressForm: false,
        isShowingBillingAddressForm: shouldShowBillingAddressForm,
        useShippingAddress: false,
      };
    }
    case types.SET_SHIPPING_INFORMATION:
      return {
        ...state,
        shippingInformation: action.payload.shippingInformation,
      };
    case types.SET_BILLING_INFORMATION:
      return {
        ...state,
        billingInformation: action.payload.billingInformation,
      };
    case types.SET_SUPPLIER_FACILITY_ID: {
      const visitedSteps = { ...state.visitedCustomerInformationSteps };
      visitedSteps[CUSTOMER_INFO_STEPS.FACILITY] = true;
      return {
        ...state,
        customerInfoStep: CUSTOMER_INFO_STEPS.FACILITY,
        supplierFacilityId: action.payload.supplierFacilityId,
        visitedCustomerInformationSteps: visitedSteps,
      };
    }
    case types.SET_CUSTOMER_INFO_STEP: {
      const visitedSteps = { ...state.visitedCustomerInformationSteps };
      visitedSteps[action.payload.step] = true;
      return {
        ...state,
        customerInfoStep: action.payload.step,
        visitedCustomerInformationSteps: visitedSteps,
      };
    }
    case types.SET_SHIPPING_TYPE:
      return {
        ...state,
        shippingType: action.payload.shippingType,
        shippingAccount:
          action.payload.shippingType === SHIPPING_TYPE.CUSTOMER_ACCOUNT
            ? state.shippingAccount
            : undefined,
        shippingRate:
          action.payload.shippingType === SHIPPING_TYPE.SUPPLIER_ACCOUNT
            ? state.paymentMethod === PAYMENT_METHOD.PURCHASE_ORDER
              ? SHIPPING_METHOD_RATES[0]
              : state.shippingRate
            : undefined,
      };
    case types.SET_SHIPPING_RATE:
      return {
        ...state,
        shippingRate: action.payload.shippingRate,
      };
    case types.SELECT_ADD_ON: {
      const newSelectedOptions = { ...state.selectedOptions };
      const newSelectedOption = {
        ...newSelectedOptions[action.payload.quoteItemId],
      };
      const newSelectedOptionAddOns = { ...newSelectedOption.addOns };
      if (newSelectedOptionAddOns[action.payload.addOn.addOnId]) {
        delete newSelectedOptionAddOns[action.payload.addOn.addOnId];
      } else {
        newSelectedOptionAddOns[action.payload.addOn.addOnId] =
          action.payload.addOn;
      }

      return {
        ...state,
        selectedOptions: {
          ...newSelectedOptions,
          [action.payload.quoteItemId]: {
            ...newSelectedOption,
            addOns: newSelectedOptionAddOns,
          },
        },
      };
    }
    case types.ADJUSTMENT_ADD: {
      const { quoteItemId } = action.payload;
      const newAdjustments = { ...state.adjustments };
      const newSelectedOptions = { ...state.selectedOptions };
      delete newSelectedOptions[quoteItemId];
      newAdjustments[quoteItemId] = {
        id: '',
        quantity: 0,
        unitPrice: '',
        totalPrice: '',
      };
      return {
        ...state,
        adjustments: newAdjustments,
        selectedOptions: newSelectedOptions,
      };
    }
    case types.ADJUSTMENT_UPDATE: {
      const { quoteItemId, data } = action.payload;
      const newAdjustments = { ...state.adjustments };
      const newAdjustment = { ...newAdjustments[quoteItemId] };
      for (const key in data) newAdjustment[key] = data[key];
      newAdjustment.totalPrice =
        !isNaN(parseInt(String(newAdjustment.quantity))) &&
        !isNaN(parseFloat(newAdjustment.unitPrice))
          ? (
              parseInt(String(newAdjustment.quantity)) *
              parseFloat(newAdjustment.unitPrice)
            ).toFixed(2)
          : newAdjustment.totalPrice || '0';
      newAdjustments[quoteItemId] = newAdjustment;
      return {
        ...state,
        adjustments: newAdjustments,
      };
    }
    case types.ADJUSTMENT_DELETE: {
      const { quoteItemId } = action.payload;
      const newAdjustments = { ...state.adjustments };
      delete newAdjustments[quoteItemId];
      return {
        ...state,
        adjustments: newAdjustments,
      };
    }
    case types.ADDITIONAL_CHARGE_ADD: {
      const { additionalChargeId, quoteItemId } = action.payload;
      const newAdditionalCharges = { ...state.additionalCharges };
      const newQuoteItemAdditionalCharges = {
        ...(newAdditionalCharges[quoteItemId] ?? {}),
        [additionalChargeId]: {
          id: additionalChargeId,
          name: '',
          price: '',
        },
      };
      newAdditionalCharges[quoteItemId] = newQuoteItemAdditionalCharges;
      return {
        ...state,
        additionalCharges: newAdditionalCharges,
      };
    }
    case types.ADDITIONAL_CHARGE_UPDATE: {
      const { additionalChargeId, quoteItemId, data } = action.payload;
      const newAdditionalCharges = { ...state.additionalCharges };
      const newQuoteItemAdditionalCharges = {
        ...(newAdditionalCharges[quoteItemId] ?? {}),
      };
      for (const key in data)
        newQuoteItemAdditionalCharges[additionalChargeId][key] = data[key];
      newAdditionalCharges[quoteItemId] = newQuoteItemAdditionalCharges;
      return {
        ...state,
        additionalCharges: newAdditionalCharges,
      };
    }
    case types.ADDITIONAL_CHARGE_DELETE: {
      const { additionalChargeId, quoteItemId } = action.payload;
      const newAdditionalCharges = { ...state.additionalCharges };
      const newQuoteItemAdditionalCharges = {
        ...(newAdditionalCharges[quoteItemId] ?? {}),
      };
      delete newQuoteItemAdditionalCharges[additionalChargeId];
      newAdditionalCharges[quoteItemId] = newQuoteItemAdditionalCharges;
      return {
        ...state,
        additionalCharges: newAdditionalCharges,
      };
    }
    case types.SET_LEAD_TIME: {
      const {
        quoteItemId,
        leadTime: manualLeadTime,
        shipsOn,
        quantityId,
        expediteId,
      } = action.payload;

      const newAdjustedLeadTimes = { ...state.adjustedLeadTimes };
      newAdjustedLeadTimes[expediteId || quantityId] = manualLeadTime;
      const newAdjustedShipsOn = { ...state.adjustedShipsOn };
      newAdjustedShipsOn[expediteId || quantityId] = shipsOn;

      // update selected option if there is one
      const selectedOption = Object.values(state.selectedOptions).find(
        (option) =>
          option.quantity.id === quantityId &&
          option.expedite?.id === expediteId,
      );

      const selectedOptions = selectedOption
        ? {
            ...state.selectedOptions,
            [quoteItemId]: {
              ...selectedOption,
              manualLeadTime,
              shipsOn,
            },
          }
        : state.selectedOptions;

      return {
        ...state,
        adjustedLeadTimes: newAdjustedLeadTimes,
        adjustedShipsOn: newAdjustedShipsOn,
        selectedOptions,
      };
    }
    case types.SET_LEAD_TIME_BULK: {
      const { leadTime, shipsOn } = action.payload;

      // Update all selected options with the new lead time and/or ships on date
      const newAdjustedLeadTimes = { ...state.adjustedLeadTimes };
      const newAdjustedShipsOn = { ...state.adjustedShipsOn };
      const newSelectedOptions = { ...state.selectedOptions };

      Object.entries(state.selectedOptions).forEach(([quoteItemId, option]) => {
        // Determine the key for adjustedLeadTimes: expediteId if present, otherwise quantityId
        const key = option.expedite?.id ?? option.quantity.id;
        newAdjustedLeadTimes[key] = leadTime;
        newAdjustedShipsOn[key] = shipsOn;

        // Update manualLeadTime in the selected option
        newSelectedOptions[Number(quoteItemId)] = {
          ...option,
          manualLeadTime: leadTime,
          shipsOn,
        };
      });

      // Update all adjustments' leadTime to the new value
      const newAdjustments = { ...state.adjustments };
      Object.entries(state.adjustments).forEach(([quoteItemId, adjustment]) => {
        newAdjustments[Number(quoteItemId)] = {
          ...adjustment,
          leadTime: leadTime ?? '',
          shipsOn: shipsOn ?? '',
        };
      });

      return {
        ...state,
        selectedOptions: newSelectedOptions,
        adjustedLeadTimes: newAdjustedLeadTimes,
        adjustedShipsOn: newAdjustedShipsOn,
        adjustments: newAdjustments,
      };
    }
    case types.SET_OPTIONS_REQUEST:
      return {
        ...state,
        isSettingOptions: true,
      };
    case types.SET_OPTIONS_SUCCESS:
      return {
        ...state,
        isSettingOptions: false,
        additionalCharges: {},
        tab: 'customer_information',
        quoteItems: action.payload.quoteItems ?? state.quoteItems,
      };
    case types.SET_CARD_DETAILS:
      return {
        ...state,
        card: action.payload.card,
        stripeToken: action.payload.stripeToken,
      };
    case types.SET_PAYMENT_METHOD_OLD: {
      const visitedSteps = { ...state.visitedCustomerInformationSteps };
      const nextStep =
        state.maxSupplierFacilities > 1 && !state.isQuoteReadOnly
          ? CUSTOMER_INFO_STEPS.SUPPLIER_FACILITY
          : CUSTOMER_INFO_STEPS.FACILITY;
      visitedSteps[nextStep] = true;
      const newState = {
        ...state,
        paymentMethod: action.payload.paymentMethod,
        confirmPaymentTerms: action.payload.confirmPaymentTerms,
        purchaseOrderNumber: action.payload.purchaseOrderNumber,
        purchaseOrderFile: action.payload.purchaseOrderFile,
        customerInfoStep: nextStep,
        visitedCustomerInformationSteps: visitedSteps,
        shippingType: undefined,
        shippingRate: undefined,
        shippingAccount: undefined,
      };
      return newState;
    }
    case types.SET_PAYMENT_METHOD:
      return {
        ...state,
        paymentMethod: action.payload.paymentMethod,
      };
    case types.SET_PURCHASE_ORDER_NUMBER:
      return {
        ...state,
        purchaseOrderNumber: action.payload.purchaseOrderNumber,
      };
    case types.SET_PURCHASE_ORDER_FILE:
      return {
        ...state,
        purchaseOrderFile: action.payload.purchaseOrderFile,
      };
    case types.SET_POINT_OF_CONTACT:
      return {
        ...state,
        pointOfContact: action.payload.pointOfContact,
        confirmationEmail: action.payload.confirmationEmail,
        withConfirmation: action.payload.withConfirmation,
      };
    case types.SET_USE_SHIPPING_ADDRESS: {
      const nextStep = action.payload.useShippingAddress
        ? CUSTOMER_INFO_STEPS.SHIPPING_OPTIONS_AND_PRICING_SUMMARY
        : CUSTOMER_INFO_STEPS.BILLING_ADDRESS;
      const visitedSteps = { ...state.visitedCustomerInformationSteps };
      visitedSteps[nextStep] = true;
      const shouldShowBillingAddressForm =
        (state.paymentMethod == PAYMENT_METHOD.CREDIT_CARD &&
          state.account &&
          state.account.billingAddresses.filter(
            (address) => address.address && address.address.address1,
          ).length === 0) ||
        (state.paymentMethod == PAYMENT_METHOD.CREDIT_CARD && !state.account);
      return {
        ...state,
        useShippingAddress: action.payload.useShippingAddress,
        billingInformationType: action.payload.useShippingAddress
          ? BILLING_INFO_TYPES.ONE_TIME_ADDRESS
          : BILLING_INFO_TYPES.NONE,
        billingInformation: action.payload.useShippingAddress
          ? state.shippingInformationType == SHIPPING_INFO_TYPES.FACILITY
            ? convertFacilityToShippingInformation(
                state.account!,
                state.contact!,
                state.facility!,
              )
            : { ...state.shippingInformation }
          : null,
        customerInfoStep: nextStep,
        visitedCustomerInformationSteps: visitedSteps,
        isShowingBillingAddressForm:
          !action.payload.useShippingAddress && shouldShowBillingAddressForm,
      };
    }
    case types.CREATE_ORDER_REQUEST:
      return {
        ...state,
        isCreatingOrder: true,
      };
    case types.CREATE_ORDER_SUCCESS:
      return {
        ...state,
        isCreatingOrder: false,
        isOpen: false,
      };
    case types.CREATE_ORDER_ERROR:
      return {
        ...state,
        isCreatingOrder: false,
      };
    case types.SELECT_NEWLY_ADDED_BILLING_ADDRESS_SUCCESS: {
      const { billingAddress } = action.payload;
      const account = {
        ...state.account,
        billingAddresses: [
          ...(state.account?.billingAddresses ?? []),
          billingAddress,
        ],
      };
      const visitedSteps = { ...state.visitedCustomerInformationSteps };
      visitedSteps[CUSTOMER_INFO_STEPS.SHIPPING_OPTIONS_AND_PRICING_SUMMARY] =
        true;
      return {
        ...state,
        account,
        billingAddress,
        billingInformationType: BILLING_INFO_TYPES.BILLING_ADDRESS,
        isShowingBillingAddressForm: false,
        customerInfoStep:
          CUSTOMER_INFO_STEPS.SHIPPING_OPTIONS_AND_PRICING_SUMMARY,
        visitedCustomerInformationSteps: visitedSteps,
      };
    }
    case types.SELECT_NEWLY_ADDED_FACILITY_SUCCESS: {
      const { facility } = action.payload;
      const account = {
        ...state.account,
        facilities: [...(state.account?.facilities ?? []), facility],
      };
      const visitedSteps = { ...state.visitedCustomerInformationSteps };
      visitedSteps[CUSTOMER_INFO_STEPS.BILLING_ADDRESS] = true;
      const shouldShowBillingAddressForm =
        (state.paymentMethod == PAYMENT_METHOD.CREDIT_CARD &&
          state.account &&
          state.account.billingAddresses.filter(
            (address) => address.address && address.address.address1,
          ).length === 0) ||
        (state.paymentMethod == PAYMENT_METHOD.CREDIT_CARD && !state.account);
      return {
        ...state,
        account: account,
        facility: facility,
        customerInfoStep: CUSTOMER_INFO_STEPS.BILLING_ADDRESS,
        shippingInformationType: SHIPPING_INFO_TYPES.FACILITY,
        visitedCustomerInformationSteps: visitedSteps,
        isShowingBillingAddressForm: shouldShowBillingAddressForm,
        useShippingAddress: false,
      };
    }
    case types.SELECT_NEWLY_ADDED_BILLING_ADDRESS_INFORMATION: {
      return {
        ...state,
        billingInformation: action.payload.address,
        billingInformationType: BILLING_INFO_TYPES.ONE_TIME_ADDRESS,
        isShowingBillingAddressForm: false,
      };
    }
    case types.SELECT_NEWLY_ADDED_SHIPPING_ADDRESS_INFORMATION: {
      const shouldShowBillingAddressForm =
        (state.paymentMethod == PAYMENT_METHOD.CREDIT_CARD &&
          state.account &&
          state.account.billingAddresses.filter(
            (address) => address.address && address.address.address1,
          ).length === 0) ||
        (state.paymentMethod == PAYMENT_METHOD.CREDIT_CARD && !state.account);
      return {
        ...state,
        shippingInformation: action.payload.address,
        shippingInformationType: SHIPPING_INFO_TYPES.ONE_TIME_ADDRESS,
        isShowingShippingAddressForm: false,
        isShowingBillingAddressForm: shouldShowBillingAddressForm,
        useShippingAddress: false,
      };
    }
    case types.SET_SEARCH_QUERY: {
      const searchQuery = action.payload.searchQuery;
      const filteredQuoteItems = state.quoteItems.filter(
        (quoteItem) =>
          !searchQuery ||
          (quoteItem.rootComponent.partNumber &&
            quoteItem.rootComponent.partNumber
              .toLowerCase()
              .includes(searchQuery.toLowerCase())) ||
          (quoteItem.rootComponent.revision &&
            quoteItem.rootComponent.revision
              .toLowerCase()
              .includes(searchQuery.toLowerCase())) ||
          (quoteItem.rootComponent.part.filename &&
            quoteItem.rootComponent.part.filename
              .toLowerCase()
              .includes(searchQuery.toLowerCase())),
      );
      return {
        ...state,
        filteredQuoteItems,
        searchQuery,
      };
    }
    case types.VALIDATE_CC_REQUEST:
      return {
        ...state,
        isValidatingCC: true,
      };
    case types.VALIDATE_CC_SUCCESS:
    case types.VALIDATE_CC_ERROR:
      return {
        ...state,
        isValidatingCC: false,
      };
    case types.SHIPPING_ADDRESS_FORM_SHOW:
      return {
        ...state,
        isShowingShippingAddressForm: true,
      };
    case types.SHIPPING_ADDRESS_FORM_HIDE:
      return {
        ...state,
        isShowingShippingAddressForm: false,
      };
    case types.BILLING_ADDRESS_FORM_SHOW:
      return {
        ...state,
        isShowingBillingAddressForm: true,
      };
    case types.BILLING_ADDRESS_FORM_HIDE:
      return {
        ...state,
        isShowingBillingAddressForm: false,
      };
    case types.SET_EXPANDED_CUSTOMER_INFO_STEP_INDICES:
      return {
        ...state,
        expandedCustomerInfoStepIndices:
          action.payload.expandedCustomerInfoStepIndices,
      };
    default:
      return state;
  }
};

export default reducer;

const getScope = (state: RootState) =>
  state.modules.quotes.facilitateOrderDrawer as FacilitateOrderDrawerState;

export const isFacilitateOrderDrawerOpen = (state: RootState) =>
  getScope(state).isOpen;

export const getFacility = (state: RootState) => getScope(state).facility;

export const getFacilities = (state: RootState) => {
  return (
    getScope(state).account?.facilities.filter(
      (facility) => facility.address && facility.address.address1,
    ) ?? []
  );
};

export const getPaymentMethod = (state: RootState) =>
  getScope(state).paymentMethod;

export const getShippingInformation = (state: RootState) =>
  getScope(state).shippingInformation;

export const getShippingInformationType = (state: RootState) =>
  getScope(state).shippingInformationType;

export const getBillingAddresses = (state: RootState) =>
  getScope(state).account?.billingAddresses.filter(
    (address) => address.address && address.address.address1,
  ) ?? [];

export const getBillingAddress = (state: RootState) =>
  getScope(state).billingAddress;

export const getBillingInformationType = (state: RootState) =>
  getScope(state).billingInformationType;

export const getBillingInformation = (state: RootState) =>
  getScope(state).billingInformation;

export const getSelectedOption = (state: RootState, quoteItemId: number) => {
  return getScope(state).selectedOptions[quoteItemId];
};

export const getSelectedOptions = (state: RootState) =>
  getScope(state).selectedOptions;

export const getNumberOfSelectedParts = (state: RootState) => {
  return (
    Object.keys(getScope(state).selectedOptions).length +
    Object.keys(getScope(state).adjustments).length
  );
};

export const getIsAddressValidationModalOpen = (state: RootState) =>
  getScope(state).isAddressValidationModalOpen;

export const getIsValidatingCC = (state: RootState) =>
  getScope(state).isValidatingCC;

export const getOriginalAddress = (state: RootState) =>
  getScope(state).originalAddress;

export const getSuggestedAddresses = (state: RootState) =>
  getScope(state).suggestedAddresses;

export const getTab = (state: RootState) => getScope(state).tab;

export const getCustomerInfoStep = (state: RootState) =>
  getScope(state).customerInfoStep;

export const getShippingAccount = (state: RootState) =>
  getScope(state).shippingAccount;

export const getShippingRates = (state: RootState) =>
  getScope(state).shippingRates;

export const getShippingRate = (state: RootState) =>
  getScope(state).shippingRate;

export const getShippingType = (state: RootState) =>
  getScope(state).shippingType;

export const getIsFetchingShippingRates = (state: RootState) =>
  getScope(state).isFetchingShippingRates;

export const getAdjustmentsCost = (state: RootState) =>
  Object.values(getScope(state).adjustments).reduce(
    (acc, curr) => acc + parseFloat(curr.totalPrice),
    0.0,
  );

export const getAllAdditionalCharges = (state: RootState) =>
  getScope(state).additionalCharges;

export const getAdditionalChargesMap = (
  state: RootState,
  quoteItemId: number,
) => getScope(state).additionalCharges[quoteItemId] || {};

export const getAdditionalCharges = (state: RootState, quoteItemId: number) =>
  Object.values(getAdditionalChargesMap(state, quoteItemId));

export const getFlattenedAdditionalCharges = (state: RootState) => {
  const ret: AdditionalCharge[] = [];
  Object.values(getAllAdditionalCharges(state)).forEach((item) => {
    ret.push(...Object.values(item));
  });
  return ret;
};

export const getAdjustment = (state: RootState, quoteItemId: number) =>
  getScope(state).adjustments[quoteItemId];

export const getAdjustments = (state: RootState) => getScope(state).adjustments;

export const getFlattenedAdjustments = (state: RootState) =>
  Object.values(getAdjustments(state));

//the cost of all the additional charges
export const getAdditionalChargesCost = (state: RootState) => {
  const additionalChargesMap = getAllAdditionalCharges(state);
  const selectedOptions = getSelectedOptions(state);
  const adjustments = getAdjustments(state);
  const additionalChargesForSelectedOptions = Object.entries(
    additionalChargesMap,
  )
    .filter(
      ([quoteItemId]) =>
        selectedOptions[Number(quoteItemId)] ||
        adjustments[Number(quoteItemId)],
    )
    .map(([, item]) => item);

  const additionalCharges = additionalChargesForSelectedOptions.flatMap(
    (item) => Object.values(item),
  );

  const ret = additionalCharges.reduce(
    (acc, curr) => acc + parseFloat(curr.price || '0'),
    0.0,
  );

  return ret;
};

//the cost of all the selected addons
export const getAddOnsCost = (state: RootState) => {
  const addOns: SelectedOption['addOns'][string][] = [];
  Object.values(getSelectedOptions(state)).forEach((option) =>
    addOns.push(...Object.values(option.addOns)),
  );
  return addOns.reduce(
    (acc, curr) => acc + parseFloat(curr.manualPrice ?? curr.price),
    0.0,
  );
};

//the cost of the selected quantities or expedites
export const getSubtotalCost = (state: RootState) => {
  return (
    Object.values(getSelectedOptions(state)).reduce(
      (total, curr) =>
        parseFloat(
          curr.expedite ? curr.expedite.totalPrice : curr.quantity.totalPrice,
        ) + total,
      0,
    ) +
    getAdjustmentsCost(state) +
    getAdditionalChargesCost(state) +
    getAddOnsCost(state)
  );
};

export const getTaxCost = (state: RootState, quote: LiteQuote) => {
  const deprecatedTaxCost = parseFloat((quote || {}).taxCost);
  if (deprecatedTaxCost) return deprecatedTaxCost;
  const quoteTaxCost = percentage(
    getSubtotalCost(state),
    quote.taxRate ? parseFloat(quote.taxRate) : 0,
  );
  const shippingRate = getShippingRate(state);
  const shippingTaxCost = shippingRate
    ? percentage(shippingRate.price, getScope(state).shippingTaxRate)
    : 0;
  return quoteTaxCost + shippingTaxCost;
};

export const getShippingCost = (state: RootState) =>
  getShippingRate(state)?.price ?? 0;

export const getTotalCost = (state: RootState, quote: LiteQuote) =>
  (
    getSubtotalCost(state) +
    getShippingCost(state) +
    getTaxCost(state, quote)
  ).toFixed(2);

export const getAccount = (state: RootState) => getScope(state).account;

export const getContact = (state: RootState) => getScope(state).contact;

export const getCustomer = (state: RootState) => getScope(state).customer;

export const getPointOfContact = (state: RootState) =>
  getScope(state).pointOfContact;

export const getSupplierFacilityId = (state: RootState) =>
  getScope(state).supplierFacilityId;

export const getSupplierFacilities = (state: RootState) =>
  getScope(state).supplierFacilities;

export const getIsSettingOptions = (state: RootState) =>
  getScope(state).isSettingOptions;

export const getFieldErrors = (state: RootState) => {
  const additionalCharges = getAllAdditionalCharges(state);
  const adjustments = getAdjustments(state);

  // Returns an object mapping quoteItemId to a boolean indicating if there is an invalid adjustment or additional charge
  const errors: Record<number, boolean> = {};

  // Check adjustments for errors
  Object.entries(adjustments).forEach(([quoteItemId, adjustment]) => {
    // An adjustment is invalid if quantity or unitPrice is missing or not a valid number
    const quantityValid =
      adjustment.quantity !== undefined &&
      adjustment.quantity !== '' &&
      !isNaN(Number(adjustment.quantity)) &&
      Number(adjustment.quantity) > 0;
    const unitPriceValid =
      adjustment.unitPrice !== undefined &&
      adjustment.unitPrice !== '' &&
      !isNaN(Number(adjustment.unitPrice)) &&
      Number(adjustment.unitPrice) >= 0;
    const leadTimeValid =
      adjustment.leadTime != null &&
      adjustment.leadTime !== '' &&
      !isNaN(Number(adjustment.leadTime)) &&
      Number(adjustment.leadTime) >= 0;
    if (!quantityValid || !unitPriceValid || !leadTimeValid) {
      errors[Number(quoteItemId)] = true;
    }
  });

  // Check additional charges for errors
  Object.entries(additionalCharges).forEach(([quoteItemId, charges]) => {
    Object.values(charges).forEach((charge) => {
      // An additional charge is invalid if name is missing/empty or price is missing/invalid
      const nameValid = charge.name && charge.name.trim() !== '';
      const priceValid =
        charge.price !== undefined &&
        charge.price !== '' &&
        !isNaN(Number(charge.price)) &&
        Number(charge.price) >= 0;
      if (!nameValid || !priceValid) {
        errors[Number(quoteItemId)] = true;
      }
    });
  });

  return errors;
};

export const getAreAdjustmentsAndAdditionalChargesValid = (
  state: RootState,
) => {
  const errors = getFieldErrors(state);
  return Object.keys(errors).length === 0;
};

export const getStripeToken = (state: RootState) => getScope(state).stripeToken;

export const getCard = (state: RootState) => getScope(state).card;

export const getPurchaseOrderNumber = (state: RootState) =>
  getScope(state).purchaseOrderNumber;

export const getPurchaseOrderFile = (state: RootState) =>
  getScope(state).purchaseOrderFile;

export const getConfirmationEmail = (state: RootState) =>
  getScope(state).confirmationEmail;

export const getWithConfirmation = (state: RootState) =>
  getScope(state).withConfirmation;

export const getVisitedCustomerInformationSteps = (state: RootState) =>
  getScope(state).visitedCustomerInformationSteps;

export const getQuoteItems = (state: RootState) => getScope(state).quoteItems;

export const getCanSubmit = (state: RootState) => {
  const paymentMethod = getPaymentMethod(state);
  if (paymentMethod === undefined) return false;
  else if (paymentMethod === PAYMENT_METHOD.NONE) return true;
  else if (
    paymentMethod == PAYMENT_METHOD.PURCHASE_ORDER &&
    getPurchaseOrderNumber(state) &&
    getCustomerInfoStep(state) !== CUSTOMER_INFO_STEPS.PAYMENT_DETAILS
  ) {
    return true;
  } else if (paymentMethod == PAYMENT_METHOD.CREDIT_CARD && getCard(state)) {
    return true;
  }
  return false;
};

export const getIsUsingShippingAddressForBilling = (state: RootState) =>
  getScope(state).useShippingAddress;

export const getIsCreatingOrder = (state: RootState) =>
  getScope(state).isCreatingOrder;

export const getHasValidShipping = (state: RootState) => {
  const shippingType = getScope(state).shippingType;
  const shippingRate = getScope(state).shippingRate;
  const shippingAccount = getScope(state).shippingAccount;
  if (shippingType === null) {
    return true;
  } else if (shippingType === SHIPPING_TYPE.PICKUP) {
    return true;
  } else if (shippingType === SHIPPING_TYPE.SUPPLIER_ACCOUNT && shippingRate) {
    return true;
  } else if (
    shippingType === SHIPPING_TYPE.CUSTOMER_ACCOUNT &&
    shippingAccount &&
    shippingAccount.customersAccountNumber !== undefined
  ) {
    return true;
  }
  return false;
};

export const getIsShowingShippingAddressForm = (state: RootState) =>
  getScope(state).isShowingShippingAddressForm;

export const getIsShowingBillingAddressForm = (state: RootState) =>
  getScope(state).isShowingBillingAddressForm;

export const getContactEmail = (state: RootState) =>
  getScope(state).contact?.email ?? undefined;

export const getDrawerMode = (state: RootState) => getScope(state).drawerMode;

export const getAdjustedLeadTime = (state: RootState, key: number) =>
  getScope(state).adjustedLeadTimes[key];

export const getSearchQuery = (state: RootState) => getScope(state).searchQuery;

export const getFilteredQuoteItems = (state: RootState) =>
  getScope(state).filteredQuoteItems ?? getQuoteItems(state);

export const getAdjustedShipsOn = (state: RootState, key: number) =>
  getScope(state).adjustedShipsOn[key];

export const getIsQuoteReadOnly = (state: RootState) =>
  getScope(state).isQuoteReadOnly;

export const getExpandedCustomerInfoStepIndices = (state: RootState) =>
  getScope(state).expandedCustomerInfoStepIndices;

export const getIsCustomerInfoValid = (state: RootState) => {
  const {
    billingInformation,
    paymentMethod,
    purchaseOrderNumber,
    shippingAccount,
    shippingType,
    stripeToken,
  } = getScope(state);
  if (paymentMethod === PAYMENT_METHOD.PURCHASE_ORDER) {
    if (!purchaseOrderNumber) {
      return false;
    }
  }
  if (paymentMethod === PAYMENT_METHOD.CREDIT_CARD) {
    if (!billingInformation || !stripeToken) {
      return false;
    }
  }
  if (shippingType === SHIPPING_TYPE.CUSTOMER_ACCOUNT) {
    if (!shippingAccount?.customersAccountNumber) {
      return false;
    }
  }
  return true;
};

export const getPointOfContactErrors = (state: RootState) => {
  const confirmationEmail = getConfirmationEmail(state);
  if (confirmationEmail && !isValidEmail(confirmationEmail)) {
    return {
      confirmationEmail: 'Please enter a valid email address',
    };
  }
  return {};
};
